import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { useStore } from "../contexts/StoreContext";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchCustomers,
  fetchPasses,
  exportCustomers,
  exportTransactions,
} from "../api/queries";
import { updateCustomer } from "../api/mutations";
import { buildCustomerUpdatePayload } from "../utils/customerPayload";
import Select from "react-select";
import {
  COUNTRY_OPTIONS,
  customStyles,
  filterCountryOption,
  getCountryName,
} from "../constants/countries";
import { Link } from "react-router-dom";
import { debounce } from "lodash";
import {
  UserGroupIcon,
  PencilIcon,
  TrashIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";
import EmptyState from "../components/EmptyState";
import DeleteAlert from "../components/DeleteAlert";
import { deleteCustomer } from "../api/mutations";
import Header from "../components/Header";
import TrialEndedAlert from "../components/TrialEndedAlert";
import Drawer from "../blocks/drawer";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const startOfDay = (d) => {
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  return r;
};

const endOfDay = (d) => {
  const r = new Date(d);
  r.setHours(23, 59, 59, 999);
  return r;
};

const EXPORT_PRESETS = [
  {
    id: "today",
    range: () => {
      const now = new Date();
      return [startOfDay(now), endOfDay(now)];
    },
  },
  {
    id: "yesterday",
    range: () => {
      const d = new Date();
      d.setDate(d.getDate() - 1);
      return [startOfDay(d), endOfDay(d)];
    },
  },
  {
    id: "last7Days",
    range: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 6);
      return [startOfDay(start), endOfDay(end)];
    },
  },
  {
    id: "last30Days",
    range: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 29);
      return [startOfDay(start), endOfDay(end)];
    },
  },
  {
    id: "thisMonth",
    range: () => {
      const d = new Date();
      const start = new Date(d.getFullYear(), d.getMonth(), 1);
      return [startOfDay(start), endOfDay(d)];
    },
  },
  {
    id: "lastMonth",
    range: () => {
      const d = new Date();
      const start = new Date(d.getFullYear(), d.getMonth() - 1, 1);
      const end = new Date(d.getFullYear(), d.getMonth(), 0);
      return [startOfDay(start), endOfDay(end)];
    },
  },
  {
    id: "thisYear",
    range: () => {
      const d = new Date();
      const start = new Date(d.getFullYear(), 0, 1);
      return [startOfDay(start), endOfDay(d)];
    },
  },
  {
    id: "allTime",
    range: () => [null, null],
  },
];

export default function Customers() {
  const { t, language } = useLanguage();
  const { selectedStore, canManageStore } = useStore();
  const queryClient = useQueryClient();
  const [editingCustomer, setEditingCustomer] = useState(null);
  // The record as it was loaded, kept untouched so the save can diff
  // against it. editingCustomer is mutated by every keystroke and by the
  // custom-field seeding effect, so it cannot serve as its own baseline.
  const [originalCustomer, setOriginalCustomer] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPass, setSelectedPass] = useState("all");
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const [deleteCustomerId, setDeleteCustomerId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [isTrialEndedAlertOpen, setIsTrialEndedAlertOpen] = useState(false);
  const [isExportPanelOpen, setIsExportPanelOpen] = useState(false);
  const [exportType, setExportType] = useState("customers");
  const [exportStartDate, setExportStartDate] = useState(null);
  const [exportEndDate, setExportEndDate] = useState(null);
  const [activePreset, setActivePreset] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const userTimezone =
    Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

  const applyPreset = (presetId) => {
    const preset = EXPORT_PRESETS.find((p) => p.id === presetId);
    if (!preset) return;
    const [start, end] = preset.range();
    setExportStartDate(start);
    setExportEndDate(end);
    setActivePreset(presetId);
    setExportError(null);
  };

  const handleRangeChange = (dates) => {
    const [start, end] = dates;
    setExportStartDate(start);
    setExportEndDate(end);
    setActivePreset(null);
    setExportError(null);
  };

  const debouncedSearch = useCallback(
    debounce((term) => {
      setDebouncedSearchTerm(term);
    }, 500),
    [],
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (value.length >= 3 || value.length === 0) {
      debouncedSearch(value);
    }
  };

  const handleSearchBlur = () => {
    if (searchTerm.length >= 3 || searchTerm.length === 0) {
      debouncedSearch.cancel();
      setDebouncedSearchTerm(searchTerm);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedStore?.id]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedPass]);

  const { data: customersData, isLoading } = useQuery({
    queryKey: [
      "customers",
      selectedStore?.id,
      currentPage,
      debouncedSearchTerm,
      selectedPass,
    ],
    queryFn: () =>
      fetchCustomers(
        selectedStore?.id,
        currentPage,
        debouncedSearchTerm.length >= 3 ? debouncedSearchTerm : null,
        selectedPass,
      ),
    enabled: !!selectedStore?.id,
  });

  const customers = customersData?.customers || [];
  const pagination = customersData?.pagination || {
    current_page: 1,
    total_pages: 1,
    total_count: 0,
    next_page: null,
    prev_page: null,
  };

  const { data: passes } = useQuery({
    queryKey: ["passes", selectedStore?.id],
    queryFn: () => fetchPasses(selectedStore?.id),
    enabled: !!selectedStore?.id,
  });

  const handlePreviousPage = () => {
    if (pagination.prev_page) {
      setCurrentPage(pagination.prev_page);
    }
  };

  const handleNextPage = () => {
    if (pagination.next_page) {
      setCurrentPage(pagination.next_page);
    }
  };

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const renderPagination = () => {
    if (pagination.total_count === 0) return null;

    const getPageNumbers = () => {
      const pageNumbers = [];
      const maxVisiblePages = 5;

      let startPage = Math.max(
        1,
        pagination.current_page - Math.floor(maxVisiblePages / 2),
      );
      let endPage = Math.min(
        pagination.total_pages,
        startPage + maxVisiblePages - 1,
      );

      if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      return { pageNumbers, startPage, endPage };
    };

    const { pageNumbers, startPage, endPage } = getPageNumbers();

    return (
      <div className="flex items-center justify-between border-t-0 bg-white px-4 py-3 sm:px-6">
        {pagination.total_pages > 1 && (
          <div className="flex flex-1 justify-between sm:hidden">
            <button
              onClick={handlePreviousPage}
              disabled={!pagination.prev_page}
              className={`relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium ${
                !pagination.prev_page
                  ? "text-gray-300 cursor-not-allowed"
                  : "text-gray-700 hover:bg-gray-50"
              }`}
            >
              {t("pagination.previous")}
            </button>
            <button
              onClick={handleNextPage}
              disabled={!pagination.next_page}
              className={`relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium ${
                !pagination.next_page
                  ? "text-gray-300 cursor-not-allowed"
                  : "text-gray-700 hover:bg-gray-50"
              }`}
            >
              {t("pagination.next")}
            </button>
          </div>
        )}

        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          <div>
            <p
              className="text-sm text-gray-700"
              dangerouslySetInnerHTML={{
                __html:
                  pagination.total_count < 25
                    ? t("pagination.summary_single", {
                        total: pagination.total_count,
                      })
                    : t("pagination.summary", {
                        from:
                          pagination.total_count > 0
                            ? (pagination.current_page - 1) * 25 + 1
                            : 0,
                        to: Math.min(
                          pagination.current_page * 25,
                          pagination.total_count,
                        ),
                        total: pagination.total_count,
                      }),
              }}
            />
          </div>

          {pagination.total_pages > 1 && (
            <div>
              <nav
                className="isolate inline-flex -space-x-px rounded-md shadow-xs"
                aria-label="Pagination"
              >
                <button
                  onClick={handlePreviousPage}
                  disabled={!pagination.prev_page}
                  className={`relative inline-flex items-center rounded-l-md px-2 py-2 ${
                    !pagination.prev_page
                      ? "text-gray-300 cursor-not-allowed"
                      : "text-gray-500 hover:bg-gray-50"
                  } focus:z-20 focus:outline-offset-0`}
                >
                  <span className="sr-only">{t("pagination.previous")}</span>
                  <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                </button>

                {startPage > 1 && (
                  <>
                    <button
                      onClick={() => goToPage(1)}
                      className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                        pagination.current_page === 1
                          ? "bg-indigo-600 text-white"
                          : "text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                      } focus:z-20 focus:outline-offset-0`}
                    >
                      1
                    </button>
                    {startPage > 2 && (
                      <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300">
                        ...
                      </span>
                    )}
                  </>
                )}

                {pageNumbers.map((page) => (
                  <button
                    key={page}
                    onClick={() => goToPage(page)}
                    className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                      pagination.current_page === page
                        ? "bg-indigo-600 text-white"
                        : "text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    } focus:z-20 focus:outline-offset-0`}
                  >
                    {page}
                  </button>
                ))}

                {endPage < pagination.total_pages && (
                  <>
                    {endPage < pagination.total_pages - 1 && (
                      <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300">
                        ...
                      </span>
                    )}
                    <button
                      onClick={() => goToPage(pagination.total_pages)}
                      className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                        pagination.current_page === pagination.total_pages
                          ? "bg-indigo-600 text-white"
                          : "text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                      } focus:z-20 focus:outline-offset-0`}
                    >
                      {pagination.total_pages}
                    </button>
                  </>
                )}

                <button
                  onClick={handleNextPage}
                  disabled={!pagination.next_page}
                  className={`relative inline-flex items-center rounded-r-md px-2 py-2 ${
                    !pagination.next_page
                      ? "text-gray-300 cursor-not-allowed"
                      : "text-gray-500 hover:bg-gray-50"
                  } focus:z-20 focus:outline-offset-0`}
                >
                  <span className="sr-only">{t("pagination.next")}</span>
                  <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </nav>
            </div>
          )}
        </div>
      </div>
    );
  };

  const deleteCustomerMutation = useMutation({
    mutationFn: deleteCustomer,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["customers", selectedStore?.id],
      });
    },
    onError: (error) => {
      setError(error.response?.data?.error || "Failed to delete customer");
    },
  });

  useEffect(() => {
    if (editingCustomer && selectedStore?.custom_fields) {
      let mergedFields = {};
      selectedStore.custom_fields.forEach((field) => {
        mergedFields[field.field] = "";
      });

      const needsUpdate =
        !editingCustomer.custom_fields ||
        Object.keys(mergedFields).some(
          (key) => editingCustomer.custom_fields[key] === undefined,
        );

      if (needsUpdate) {
        setEditingCustomer((prev) => ({
          ...prev,
          custom_fields: {
            ...mergedFields,
            ...(prev.custom_fields || {}),
          },
        }));
      }
    }
  }, [selectedStore, editingCustomer?.id]);

  const updateMutation = useMutation({
    mutationFn: updateCustomer,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["customers", selectedStore?.id],
      });
      setIsModalOpen(false);
      setEditingCustomer(null);
      setOriginalCustomer(null);
      setError(null);
    },
    onError: (error) => {
      setError(error.response?.data?.error || "Failed to update customer");
    },
  });

  const handleEdit = (customer) => {
    const isInTrialPeriod = () => {
      if (!selectedStore?.trial_expires_at) return false;
      const trialEndDate = new Date(selectedStore.trial_expires_at);
      trialEndDate.setDate(trialEndDate.getDate());
      return new Date() < trialEndDate;
    };

    if (
      selectedStore?.subscription_status !== "active" &&
      selectedStore?.subscription_status !== "past_due" &&
      selectedStore?.subscription_status !== "trialing" &&
      !isInTrialPeriod()
    ) {
      setIsTrialEndedAlertOpen(true);
      return;
    }

    setEditingCustomer({ ...customer });
    setOriginalCustomer({ ...customer });
    setError(null);
    setIsModalOpen(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // A save that already succeeded clears editingCustomer, but the drawer
    // unmounts only after its 0.7s exit transition. Measured on staging: the
    // form stays in the DOM for ~700ms after close and the Save button is still
    // enabled and hit-testable for the first ~100-200ms of that, while the panel
    // is on screen. A second click there lands here with nothing to submit.
    if (!editingCustomer) return;

    const updatedData = buildCustomerUpdatePayload(
      originalCustomer,
      editingCustomer,
    );

    // Nothing was edited. Saving anyway is not free: the request regenerates
    // the wallet pass and pushes a notification to the customer's phone.
    if (Object.keys(updatedData).length === 0) {
      setIsModalOpen(false);
      setEditingCustomer(null);
      setOriginalCustomer(null);
      setError(null);
      return;
    }

    updateMutation.mutate({
      storeId: selectedStore.id,
      customerId: editingCustomer.id,
      data: updatedData,
    });
  };

  const openExportPanel = () => {
    setExportType("customers");
    setExportStartDate(null);
    setExportEndDate(null);
    setActivePreset(null);
    setExportError(null);
    setIsExportPanelOpen(true);
  };

  const handleExportSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!selectedStore?.id) return;

    // "All Time" (and any preset) is a valid selection even with null dates;
    // a custom range still requires both ends.
    if (!activePreset && (!exportStartDate || !exportEndDate)) {
      setExportError(t("customers.export.errors.rangeRequired"));
      return;
    }

    if (exportStartDate && exportEndDate && exportStartDate > exportEndDate) {
      setExportError(t("customers.export.errors.invalidRange"));
      return;
    }

    setExportError(null);
    setIsExporting(true);
    try {
      const range = {
        startAt: exportStartDate ? exportStartDate.toISOString() : null,
        endAt: exportEndDate ? exportEndDate.toISOString() : null,
      };
      if (exportType === "transactions") {
        await exportTransactions(selectedStore.id, range);
      } else {
        await exportCustomers(selectedStore.id, range);
      }
      setIsExportPanelOpen(false);
    } catch (error) {
      console.error("Export failed:", error);
      setExportError(t("customers.export.errors.failed"));
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <div className="max-w-8xl mx-auto space-y-6 lg:space-y-8">
        <Header
          icon={UserGroupIcon}
          title={t("customers.title")}
          description={t("customers.description")}
          rightSection={
            <div className="flex items-center space-x-3">
              {canManageStore && (
                <button
                  type="button"
                  onClick={openExportPanel}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-0"
                >
                  <ArrowDownTrayIcon className="h-5 w-5 text-gray-700" />
                  {t("customers.export.button")}
                </button>
              )}

              <div className="relative w-64 sm:w-72 lg:w-80 rounded-md shadow-xs">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <MagnifyingGlassIcon
                    className="h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                </div>
                <input
                  type="text"
                  name="search"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onBlur={handleSearchBlur}
                  className="block w-full rounded-md border-gray-300 pl-10 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder={t("common.search")}
                />
              </div>

              <select
                value={selectedPass}
                onChange={(e) => setSelectedPass(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              >
                <option value="all">{t("customers.filter.allPasses")}</option>
                {passes?.map((pass) => (
                  <option key={pass.id} value={pass.id}>
                    {pass.name}
                  </option>
                ))}
              </select>
            </div>
          }
        />

        {customers?.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="bg-white rounded-md shadow-sm overflow-hidden">
            <div className="max-w-8xl mx-auto pt-0">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm text-left whitespace-nowrap">
                  <thead className="bg-gray-50">
                    <tr className="font-semibold text-gray-900">
                      <th scope="col" className="p-3 pl-4">
                        {t("customers.table.uuid")}
                      </th>
                      <th scope="col" className="p-3">
                        {t("customers.table.name")}
                      </th>
                      <th scope="col" className="p-3">
                        {t("customers.table.email")}
                      </th>
                      <th scope="col" className="p-3">
                        {t("customers.table.phone")}
                      </th>
                      <th scope="col" className="p-3">
                        {t("customers.table.pass")}
                      </th>
                      <th scope="col" className="p-3 text-right">
                        {t("customers.table.points")}
                      </th>
                      <th scope="col" className="p-3">
                        {t("customers.table.joinedAt")}
                      </th>
                      <th scope="col"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {customers?.map((customer) => (
                      <tr key={customer.id} className="text-gray-500">
                        <td className="p-3 pl-4">
                          <Link
                            // The link says what the cell shows. A customer
                            // with no uuid (none are expected) falls back to
                            // the id, which CustomerShow still resolves.
                            to={`/customers/${encodeURIComponent(customer.uuid || customer.id)}`}
                            className="text-indigo-600 hover:text-indigo-800"
                          >
                            {customer.uuid || "-"}
                          </Link>
                        </td>
                        <td className="p-3 text-gray-900">
                          {customer.name || "-"}
                        </td>
                        <td className="p-3">{customer.email || "-"}</td>
                        <td className="p-3">
                          {customer.phone
                            ? `${customer.phone_country_code} ${customer.phone}`
                            : "-"}
                        </td>
                        <td className="p-3">{customer.pass?.name || "-"}</td>
                        <td className="p-3 text-right">
                          {customer.point || "0"}
                        </td>
                        <td className="p-3">
                          {new Date(customer.created_at).toLocaleDateString()}
                        </td>
                        <td
                          className={`relative p-3 pr-4 text-right space-x-4 ${canManageStore ? "" : "invisible"}`}
                        >
                          <button
                            onClick={() => handleEdit(customer)}
                            className="text-indigo-600 hover:text-indigo-900"
                          >
                            <PencilIcon
                              className="h-5 w-5"
                              aria-hidden="true"
                            />
                          </button>
                          <button
                            onClick={() => {
                              setIsDeleteAlertOpen(true);
                              setDeleteCustomerId(customer.id);
                            }}
                            className="text-red-600 hover:text-red-900"
                          >
                            <TrashIcon className="h-5 w-5" aria-hidden="true" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {renderPagination()}
            </div>
          </div>
        )}
      </div>

      <Drawer
        open={isModalOpen}
        setOpen={setIsModalOpen}
        header={t("customers.edit.title")}
        isForm={true}
        onSubmit={handleSubmit}
        onCancel={() => {
          setIsModalOpen(false);
          setEditingCustomer(null);
          setOriginalCustomer(null);
          setError(null);
        }}
        submitText={t("common.save")}
        cancelText={t("common.cancel")}
        isLoading={updateMutation.isPending}
        submitDisabled={updateMutation.isPending}
      >
        <div className="px-4 py-6 sm:px-6 space-y-4">
          {error && (
            <div className="mb-4 text-sm text-red-600 bg-red-50 p-3 rounded-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t("customers.table.name")}
            </label>
            <input
              type="text"
              value={editingCustomer?.name || ""}
              onChange={(e) => {
                setEditingCustomer((prev) => {
                  const updated = { ...prev, name: e.target.value };
                  return updated;
                });
              }}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t("customers.table.email")}
            </label>
            <input
              type="email"
              value={editingCustomer?.email || ""}
              onChange={(e) =>
                setEditingCustomer((prev) => ({
                  ...prev,
                  email: e.target.value,
                }))
              }
              className="mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t("customers.table.phone")}
            </label>
            <div className="flex gap-2">
              <div className="w-64">
                <Select
                  options={COUNTRY_OPTIONS}
                  defaultValue={
                    COUNTRY_OPTIONS.find((option) =>
                      editingCustomer?.phone_country_code?.startsWith(
                        option.value,
                      ),
                    ) || COUNTRY_OPTIONS.find((option) => option.value === "+1")
                  }
                  styles={customStyles}
                  isSearchable={true}
                  placeholder="Select country..."
                  onChange={(option) => {
                    setEditingCustomer((prev) => ({
                      ...prev,
                      phone_country_code: option.value,
                    }));
                  }}
                  formatOptionLabel={(
                    option,
                    { context, isSelected, isFocused },
                  ) => (
                    <div className="flex items-center gap-2">
                      <span>{option.label.split(" ")[0]}</span>
                      <span
                        style={{
                          color: isSelected
                            ? "white"
                            : isFocused
                              ? "#1E40AF"
                              : "#374151",
                          fontWeight: isSelected ? "bold" : "normal",
                        }}
                      >
                        {option.value}
                      </span>
                      {context === "menu" && (
                        <span
                          className="truncate"
                          style={{ color: isSelected ? "white" : "#6B7280" }}
                        >
                          {getCountryName(option, language)}
                        </span>
                      )}
                    </div>
                  )}
                  filterOption={(option, inputValue) =>
                    filterCountryOption(option, inputValue, language)
                  }
                />
              </div>
              <input
                type="tel"
                value={editingCustomer?.phone || ""}
                onChange={(e) =>
                  setEditingCustomer((prev) => ({
                    ...prev,
                    phone: e.target.value,
                  }))
                }
                className="flex-1 rounded-md border-gray-300 shadow-xs placeholder-gray-400 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                placeholder="0000000000"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t("customers.table.birthday")}
            </label>
            <input
              type="date"
              value={editingCustomer?.birthday || ""}
              onChange={(e) =>
                setEditingCustomer((prev) => ({
                  ...prev,
                  birthday: e.target.value,
                }))
              }
              className="mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t("customers.expiry.label")}
            </label>
            {/* The stored date for this entry, never one recalculated from the
                card's current rule: the two legitimately differ, which is the
                whole point of being able to set it per customer. */}
            <input
              type="date"
              value={(editingCustomer?.expires_at || "").slice(0, 10)}
              onChange={(e) =>
                setEditingCustomer((prev) => ({
                  ...prev,
                  expires_at: e.target.value,
                }))
              }
              className="mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">
              {editingCustomer?.expires_at
                ? t("customers.expiry.help")
                : t("customers.expiry.helpEmpty")}
            </p>
            {editingCustomer?.expires_at && (
              <button
                type="button"
                onClick={() =>
                  setEditingCustomer((prev) => ({ ...prev, expires_at: "" }))
                }
                className="mt-1 text-xs font-medium text-indigo-600 hover:text-indigo-500"
              >
                {t("customers.expiry.clear")}
              </button>
            )}
          </div>

          <div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={editingCustomer?.is_marketing_opted_in || false}
                id="field-marketing"
                onChange={(e) => {
                  setEditingCustomer((prev) => {
                    const updated = {
                      ...prev,
                      is_marketing_opted_in: e.target.checked,
                    };
                    return updated;
                  });
                }}
                className="rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />

              <label
                className="text-sm font-medium text-gray-700"
                htmlFor="field-marketing"
              >
                {t("forms.new.fields.types.marketing")}
              </label>
            </div>
          </div>

          {editingCustomer?.custom_fields &&
            Object.entries(editingCustomer.custom_fields).map(
              ([key, value]) => {
                const regex = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/;
                const isValidDate = regex.test(value);

                return (
                  <div key={key}>
                    <label className="block text-sm font-medium text-gray-700 capitalize">
                      {key.replace(/_/g, " ")}
                    </label>
                    <input
                      type={isValidDate ? "date" : "text"}
                      value={value}
                      onChange={(e) =>
                        setEditingCustomer((prev) => ({
                          ...prev,
                          custom_fields: {
                            ...prev.custom_fields,
                            [key]: e.target.value,
                          },
                        }))
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>
                );
              },
            )}

          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t("customers.table.points")}
            </label>
            <input
              type="number"
              value={editingCustomer?.point || 0}
              onChange={(e) =>
                setEditingCustomer((prev) => ({
                  ...prev,
                  point: parseFloat(e.target.value),
                }))
              }
              className="mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>
        </div>
      </Drawer>

      <DeleteAlert
        isOpen={isDeleteAlertOpen}
        onClose={() => setIsDeleteAlertOpen(false)}
        onConfirm={() => {
          deleteCustomerMutation.mutate({
            storeId: selectedStore.id,
            customerId: deleteCustomerId,
          });
          setIsDeleteAlertOpen(false);
        }}
      />
      <TrialEndedAlert
        isOpen={isTrialEndedAlertOpen}
        onClose={() => setIsTrialEndedAlertOpen(false)}
        subscriptionStatus={selectedStore?.subscription_status}
      />

      <Drawer
        open={isExportPanelOpen}
        setOpen={setIsExportPanelOpen}
        size="lg"
        header={t("customers.export.panel.title")}
        onSubmit={handleExportSubmit}
        onCancel={() => setIsExportPanelOpen(false)}
        submitText={
          isExporting
            ? t("customers.export.panel.exporting")
            : t("customers.export.panel.submit")
        }
        cancelText={t("common.cancel")}
        isLoading={isExporting}
        submitDisabled={
          isExporting || (!activePreset && (!exportStartDate || !exportEndDate))
        }
      >
        <div className="px-4 py-6 sm:px-6 space-y-6">
          {exportError && (
            <div className="text-sm text-red-600 bg-red-50 p-3 rounded-sm">
              {exportError}
            </div>
          )}

          <fieldset>
            <legend className="text-sm font-medium text-gray-700 mb-2">
              {t("customers.export.panel.typeLabel")}
            </legend>
            <div className="space-y-2">
              <label className="flex items-start gap-3 rounded-md border border-gray-200 p-3 cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="exportType"
                  value="customers"
                  checked={exportType === "customers"}
                  onChange={(e) => setExportType(e.target.value)}
                  className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                />
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    {t("customers.export.panel.typeCustomers")}
                  </div>
                  <div className="text-xs text-gray-500">
                    {t("customers.export.panel.typeCustomersHint")}
                  </div>
                </div>
              </label>
              <label className="flex items-start gap-3 rounded-md border border-gray-200 p-3 cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="exportType"
                  value="transactions"
                  checked={exportType === "transactions"}
                  onChange={(e) => setExportType(e.target.value)}
                  className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                />
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    {t("customers.export.panel.typeTransactions")}
                  </div>
                  <div className="text-xs text-gray-500">
                    {t("customers.export.panel.typeTransactionsHint")}
                  </div>
                </div>
              </label>
            </div>
          </fieldset>

          <div>
            <p className="block text-sm font-medium text-gray-700 mb-2">
              {t("customers.export.panel.dateRange")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex flex-row sm:flex-col flex-wrap gap-1 sm:w-36 sm:shrink-0">
                {EXPORT_PRESETS.map((preset) => (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => applyPreset(preset.id)}
                    className={`px-3 py-1.5 text-sm font-medium text-left rounded-md transition-colors ${
                      activePreset === preset.id
                        ? "bg-indigo-50 text-indigo-700"
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    {t(`customers.export.panel.presets.${preset.id}`)}
                  </button>
                ))}
              </div>
              <div className="flex-1 flex justify-center sm:justify-start">
                <DatePicker
                  selected={exportStartDate}
                  onChange={handleRangeChange}
                  startDate={exportStartDate}
                  endDate={exportEndDate}
                  selectsRange
                  inline
                  maxDate={new Date()}
                  monthsShown={1}
                />
              </div>
            </div>
          </div>

          <div className="space-y-1">
            <p className="text-xs text-gray-500">
              {t("customers.export.panel.timezoneHint", {
                timezone: userTimezone,
              })}
            </p>
          </div>
        </div>
      </Drawer>
    </>
  );
}
